import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { 
  Users, 
  CheckCircle2, 
  AlertCircle, 
  ChevronRight,
  Play,
  Info,
  Target,
  RefreshCw,
  Zap
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { toast } from 'sonner';

/**
 * Simulation Progress Card - Shows progress on Phase 4 simulations
 * NEW: Each scenario needs 2 completions with score ≥80
 * Any difficulty counts towards progress
 */
export const SimulationProgress = ({ 
  topicId,
  simulationRequirements,
  onStartSimulation
}) => {
  const navigate = useNavigate();
  
  if (!simulationRequirements?.has_simulations) {
    return null;
  }

  const { 
    simulations = [], 
    completed_scenarios = 0, 
    total_scenarios = 5,
    total_completions_done = 0,
    total_completions_needed = 10,
    required_per_scenario = 2,
    all_complete = false,
    next_simulation = null,
    guide_message = null
  } = simulationRequirements;

  // Calculate progress based on total completions
  const progressPercent = (total_completions_done / total_completions_needed) * 100;

  // Get incomplete simulations for retry
  const incompleteSimulations = simulations.filter(s => !s.completed);
  const hasIncomplete = incompleteSimulations.length > 0;

  const handleStartSimulation = (simulation) => {
    // Navigate to the main simulation page with scenario type
    // Any difficulty counts now, so we don't force a specific difficulty
    navigate(`/simulation?scenario=${simulation.scenario_type}&from_rpat=true&topic=${topicId}`);
  };

  const handleRetryAllFailed = () => {
    if (incompleteSimulations.length === 0) {
      toast.success('All simulations already complete!');
      return;
    }

    // Start with the first incomplete simulation
    const firstIncomplete = incompleteSimulations[0];
    
    // Store remaining simulations in sessionStorage for quick retry continuation
    if (incompleteSimulations.length > 1) {
      const retryQueue = incompleteSimulations.slice(1).map(s => ({
        id: s.id,
        scenario_type: s.scenario_type,
        difficulty: s.difficulty,
        title: s.title
      }));
      sessionStorage.setItem(`retry_queue_${topicId}`, JSON.stringify(retryQueue));
    }

    toast.info(`Starting ${incompleteSimulations.length} simulation${incompleteSimulations.length > 1 ? 's' : ''}`, {
      description: `First: ${firstIncomplete.title.replace(/ \(Easy\)| \(Medium\)| \(Tough\)/g, '')}`,
      duration: 3000
    });

    // Navigate to the first simulation
    handleStartSimulation(firstIncomplete);
  };

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center
            ${all_complete 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-cyan-500/20 text-cyan-400'
            }
          `}>
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              Recruiter Simulations
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-slate-500" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Complete each of the {total_scenarios} scenarios {required_per_scenario} times with score 80+. Any difficulty counts!</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h3>
            <p className="text-sm text-slate-400">
              {all_complete 
                ? `All simulations complete! (${total_completions_done}/${total_completions_needed})` 
                : `${total_completions_done}/${total_completions_needed} completions - Need 2 per scenario`
              }
            </p>
          </div>
        </div>
        
        {/* Progress Badge + Retry All Button */}
        <div className="flex items-center gap-2">
          {/* Retry All Failed Button */}
          {hasIncomplete && !all_complete && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetryAllFailed}
                    className="border-2 font-medium transition-all border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500"
                    data-testid="retry-all-failed-btn"
                  >
                    <Zap className="h-4 w-4 mr-1.5" />
                    Continue ({incompleteSimulations.length})
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Start {incompleteSimulations.length} scenarios that need more completions</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Progress Badge */}
          <div className={`
            px-3 py-1.5 rounded-full text-sm font-medium
            ${all_complete 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-cyan-500/20 text-cyan-400'
            }
          `}>
            {total_completions_done}/{total_completions_needed}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              all_complete ? 'bg-emerald-500' : 'bg-cyan-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Simulations List - Grid for 5 items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {simulations.map((sim, idx) => {
          const isCompleted = sim.completed;
          const completionsDone = sim.completions_done || 0;
          const completionsRequired = sim.completions_required || 2;
          const needsMore = sim.needs_more;  // Has some but not enough
          const highestScore = sim.highest_score;
          
          return (
            <div 
              key={sim.id}
              className={`
                flex items-center justify-between p-3 rounded-lg border transition-all
                ${isCompleted 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : needsMore
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-slate-800/50 border-slate-700/50'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${isCompleted 
                    ? 'bg-emerald-500 text-white' 
                    : needsMore
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-700 text-slate-400'
                  }
                `}>
                  {isCompleted ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : needsMore ? (
                    <span className="text-xs font-bold">{completionsDone}/{completionsRequired}</span>
                  ) : (
                    <span className="text-sm font-bold">{idx + 1}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <span className={`font-medium text-sm ${
                    isCompleted ? 'text-emerald-400' : needsMore ? 'text-amber-400' : 'text-white'
                  }`}>
                    {sim.title.replace(/ \(Easy\)| \(Medium\)| \(Tough\)/g, '')}
                  </span>
                  <p className="text-xs text-slate-400 truncate">
                    {isCompleted 
                      ? `Complete! Best: ${highestScore}% (${completionsDone}/${completionsRequired})` 
                      : needsMore
                        ? `${completionsDone}/${completionsRequired} done - Best: ${highestScore}%`
                        : `0/${completionsRequired} - Score 80+ twice`
                    }
                  </p>
                </div>
              </div>

              {isCompleted ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartSimulation(sim)}
                        className="flex-shrink-0 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                        data-testid={`retry-sim-${idx + 1}-btn`}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        {highestScore}%
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Practice more to improve your score</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleStartSimulation(sim)}
                  className={`flex-shrink-0 ${
                    needsMore
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-cyan-600 hover:bg-cyan-700'
                  } text-white`}
                  data-testid={`start-sim-${idx + 1}-btn`}
                >
                  <Play className="h-3 w-3 mr-1" />
                  {needsMore ? 'Continue' : 'Start'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Status Message */}
      {!all_complete && guide_message && (
        <div className="border rounded-lg p-3 flex items-start gap-3 bg-cyan-500/10 border-cyan-500/20">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-cyan-400" />
          <div>
            <p className="text-sm font-medium text-cyan-200">
              {guide_message.title}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {guide_message.message}
            </p>
          </div>
        </div>
      )}

      {all_complete && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-400" />
          <div>
            <p className="text-sm text-emerald-200 font-medium">
              All simulations complete! You can now proceed to the assessment.
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-emerald-400 ml-auto" />
        </div>
      )}
    </div>
  );
};

export default SimulationProgress;
